import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress } from '@mui/material';
import { DashboardService } from 'Dashboard/Domain/Services/DashboardService';
import HomeCarousel from '../Components/Dashboard/HomeCarousel';
import HomeShortcutsBar from '../Components/Dashboard/HomeShortcutsBar';
import HomeCalendarWidget from '../Components/Dashboard/HomeCalendarWidget';
import HomeBlogSection from '../Components/Dashboard/HomeBlogSection';
import HomeLatestHiresSection from '../Components/Dashboard/HomeLatestHiresSection';
import HomeBirthdayCard from '../Components/Dashboard/HomeBirthdayCard';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [hires, setHires] = useState([]);
  const [birthdays, setBirthdays] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      try {
        const [newsRes, shortcutsRes, blogRes, hiresRes, birthdaysRes] = await Promise.allSettled([
          DashboardService.getNewsCarousel(),
          DashboardService.getUserShortcuts(),
          DashboardService.getLatestBlogPosts(),
          DashboardService.getLatestHires(),
          DashboardService.getBirthdays(),
        ]);

        if (!isMounted) return;

        if (newsRes.status === 'fulfilled' && newsRes.value?.data) {
          setNews(newsRes.value.data);
        }

        if (shortcutsRes.status === 'fulfilled' && shortcutsRes.value?.data) {
          setShortcuts(shortcutsRes.value.data);
        }

        if (blogRes.status === 'fulfilled' && blogRes.value?.data) {
          setBlogPosts(blogRes.value.data);
        }

        if (hiresRes.status === 'fulfilled' && hiresRes.value?.data) {
          setHires(hiresRes.value.data);
        }

        if (birthdaysRes.status === 'fulfilled' && birthdaysRes.value?.data) {
          setBirthdays(birthdaysRes.value.data);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSaveShortcuts = async (newShortcuts) => {
    try {
      const res = await DashboardService.saveUserShortcuts(newShortcuts);
      if (res?.data) {
        setShortcuts(res.data);
        toast.success('Accesos directos actualizados correctamente.');
      } else {
        setShortcuts(newShortcuts);
      }
    } catch {
      setShortcuts(newShortcuts);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#257eb9' }} />
      </Box>
    );
  }

  const hasBirthdays = birthdays && birthdays.length > 0 && birthdays.some((b) => b && b.isToday !== false);

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.2 },
        bgcolor: '#f1f5f9',
        minHeight: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Box
        sx={{
          maxWidth: '1440px',
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, md: 2 },
        }}
      >
        {/* Top Row: Carousel (Left) + Shortcuts & Calendar (Right) */}
        <Grid container spacing={2} alignItems="stretch">
          {/* Top Left: News Carousel */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <HomeCarousel news={news} />
          </Grid>

          {/* Top Right: Shortcuts Bar + Calendar */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <HomeShortcutsBar shortcuts={shortcuts} onSaveShortcuts={handleSaveShortcuts} />
            <HomeCalendarWidget />
          </Grid>
        </Grid>

        {/* Bottom Row: Blog (Left) + Latest Hires (Center) + Birthday (Right, only if birthdays today) */}
        <Grid container spacing={2} alignItems="stretch">
          <Grid
            item
            xs={12}
            md={hasBirthdays ? 5 : 8}
            lg={hasBirthdays ? 4.8 : 8}
            sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
          >
            <HomeBlogSection posts={blogPosts} />
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            lg={4}
            sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
          >
            <HomeLatestHiresSection hires={hires} />
          </Grid>

          {hasBirthdays && (
            <Grid item xs={12} md={3} lg={3.2} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <HomeBirthdayCard birthdays={birthdays} />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
